import {
  CONTRACT_VERSION,
  EVENT_CATALOG_VERSION,
  ProductEventSchema,
  type ProductEventName,
  type ProductEventProperties,
} from '@hoedokloop/contracts';
import type { Instant } from '@hoedokloop/domain';
import type { AnalyticsTransport } from '@hoedokloop/platform-ports';

import { productChannel } from '../../platform/channel';

export function countBucket(count: number): '0' | '1_9' | '10_49' | '50_199' | 'gte_200' {
  if (count <= 0) return '0';
  if (count < 10) return '1_9';
  if (count < 50) return '10_49';
  if (count < 200) return '50_199';
  return 'gte_200';
}

export function roundBucket(round: number): '1' | '2' | '3' | '4_plus' {
  if (round <= 1) return '1';
  if (round === 2) return '2';
  if (round === 3) return '3';
  return '4_plus';
}

export function dDayBucket(
  days: number | null,
): 'past' | 'today' | '1_7' | '8_30' | '31_90' | '91_plus' | 'unknown' {
  if (days === null) return 'unknown';
  if (days < 0) return 'past';
  if (days === 0) return 'today';
  if (days <= 7) return '1_7';
  if (days <= 30) return '8_30';
  if (days <= 90) return '31_90';
  return '91_plus';
}

export function durationBucket(
  milliseconds: number,
): 'lt_5m' | '5_24m' | '25_59m' | '60_119m' | 'gte_120m' {
  const minutes = milliseconds / 60_000;
  if (minutes < 5) return 'lt_5m';
  if (minutes < 25) return '5_24m';
  if (minutes < 60) return '25_59m';
  if (minutes < 120) return '60_119m';
  return 'gte_120m';
}

export function sendProductEvent<Name extends ProductEventName>(
  analytics: AnalyticsTransport,
  name: Name,
  properties: ProductEventProperties<Name>,
): void {
  const event = ProductEventSchema.parse({
    catalogVersion: EVENT_CATALOG_VERSION,
    contractVersion: CONTRACT_VERSION,
    name,
    occurredAt: new Date().toISOString() as Instant,
    channel: productChannel,
    build: import.meta.env['VITE_BUILD_VERSION'] ?? 'local',
    properties,
  });
  void analytics.send([event]);
}

/** Dates remain on device; the transport only receives an allowlisted age bucket. */
export function sendReturnVisit(analytics: AnalyticsTransport): void {
  if (analytics.consent() !== 'granted') return;
  try {
    const today = new Date().toISOString().slice(0, 10);
    const first = localStorage.getItem('hoedokloop:first-open-day');
    if (!first) {
      localStorage.setItem('hoedokloop:first-open-day', today);
      return;
    }
    if (first === today || localStorage.getItem('hoedokloop:last-return-day') === today) return;
    const days = Math.floor((Date.parse(today) - Date.parse(first)) / 86400000);
    if (!Number.isFinite(days) || days < 1) return;
    sendProductEvent(analytics, 'return_visit', {
      daysSinceFirstOpen: days === 1 ? '1' : days <= 7 ? '2_7' : days <= 30 ? '8_30' : '31_plus',
    });
    localStorage.setItem('hoedokloop:last-return-day', today);
  } catch {
    /* Storage unavailable: no fabricated visit age. */
  }
}
